/**
 * Validation Error Parser
 *
 * Parses NestJS class-validator error messages to extract field names and constraint types,
 * then translates them using i18n.
 */

export interface ParsedValidationError {
  field: string;
  constraint: string;
  params: Record<string, string | number>;
  originalMessage: string;
}

export interface FieldErrors {
  [field: string]: string;
}

// Patterns for extracting field name and constraint from class-validator messages
const VALIDATION_PATTERNS: Array<{
  regex: RegExp;
  constraint: string;
  extractParams?: (match: RegExpMatchArray) => Record<string, string | number>;
}> = [
  // "phone must be shorter than or equal to 20 characters"
  {
    regex: /^(\w+) must be shorter than or equal to (\d+) characters?$/,
    constraint: 'maxLength',
    extractParams: (match) => ({ max: parseInt(match[2], 10) }),
  },
  // "phone must be longer than or equal to 10 characters"
  {
    regex: /^(\w+) must be longer than or equal to (\d+) characters?$/,
    constraint: 'minLength',
    extractParams: (match) => ({ min: parseInt(match[2], 10) }),
  },
  // "email must be an email"
  {
    regex: /^(\w+) must be an email$/,
    constraint: 'isEmail',
  },
  // "field must be a string"
  {
    regex: /^(\w+) must be a string$/,
    constraint: 'isString',
  },
  // "field must be a number"
  {
    regex: /^(\w+) must be a number.*$/,
    constraint: 'isNumber',
  },
  // "field should not be empty"
  {
    regex: /^(\w+) should not be empty$/,
    constraint: 'isNotEmpty',
  },
  // "field must not be empty"
  {
    regex: /^(\w+) must not be empty$/,
    constraint: 'isNotEmpty',
  },
  // "phone must be a valid phone number"
  {
    regex: /^(\w+) must be a valid phone number$/,
    constraint: 'isPhoneNumber',
  },
  // "field must be a boolean value"
  {
    regex: /^(\w+) must be a boolean value$/,
    constraint: 'isBoolean',
  },
  // "field must be a Date instance"
  {
    regex: /^(\w+) must be a Date instance$/,
    constraint: 'isDate',
  },
  // "field must be a valid ISO 8601 date string"
  {
    regex: /^(\w+) must be a valid ISO 8601 date string$/,
    constraint: 'isDateString',
  },
  // "field must be an array"
  {
    regex: /^(\w+) must be an array$/,
    constraint: 'isArray',
  },
  // "field must be a URL address"
  {
    regex: /^(\w+) must be a URL address$/,
    constraint: 'isUrl',
  },
  // "field must be a UUID"
  {
    regex: /^(\w+) must be a UUID$/,
    constraint: 'isUuid',
  },
  // "field must be a positive number"
  {
    regex: /^(\w+) must be a positive number$/,
    constraint: 'isPositive',
  },
  // "field must not be greater than X"
  {
    regex: /^(\w+) must not be greater than (\d+)$/,
    constraint: 'max',
    extractParams: (match) => ({ max: parseInt(match[2], 10) }),
  },
  // "field must not be less than X"
  {
    regex: /^(\w+) must not be less than (\d+)$/,
    constraint: 'min',
    extractParams: (match) => ({ min: parseInt(match[2], 10) }),
  },
  // "field must be one of the following values: X, Y, Z"
  {
    regex: /^(\w+) must be one of the following values: (.+)$/,
    constraint: 'isEnum',
    extractParams: (match) => ({ values: match[2] }),
  },
];

/**
 * Parse a single validation message to extract field and constraint
 */
export function parseValidationMessage(message: string): ParsedValidationError | null {
  for (const pattern of VALIDATION_PATTERNS) {
    const match = message.match(pattern.regex);
    if (match) {
      return {
        field: match[1],
        constraint: pattern.constraint,
        params: pattern.extractParams ? pattern.extractParams(match) : {},
        originalMessage: message,
      };
    }
  }

  // Fallback: try to extract field name from the beginning of the message
  const fallbackMatch = message.match(/^(\w+)\s/);
  if (fallbackMatch) {
    return {
      field: fallbackMatch[1],
      constraint: 'unknown',
      params: {},
      originalMessage: message,
    };
  }

  return null;
}

/**
 * Parse an array of validation messages
 */
export function parseValidationErrors(messages: string[]): ParsedValidationError[] {
  return messages
    .map(parseValidationMessage)
    .filter((error): error is ParsedValidationError => error !== null);
}

/**
 * Mapping from backend field names to frontend form field names
 * Add mappings here when backend and frontend use different field names
 */
const FIELD_NAME_MAP: Record<string, string> = {
  // Example: backendField: 'frontendField',
};

/**
 * Map backend field name to frontend field name
 */
export function mapFieldName(backendField: string): string {
  return FIELD_NAME_MAP[backendField] || backendField;
}

/**
 * Translate a validation error using i18n
 */
export function translateValidationMessage(
  error: ParsedValidationError,
  t: (key: string, params?: Record<string, string | number>) => string
): string {
  const translationKey = `validation.${error.constraint}`;
  const translated = t(translationKey, error.params);

  // If translation is the same as the key, use the original message
  if (translated === translationKey) {
    return error.originalMessage;
  }

  return translated;
}

/**
 * Convert API error response to field errors map
 */
export function apiErrorToFieldErrors(
  error: unknown,
  t: (key: string, params?: Record<string, string | number>) => string
): FieldErrors {
  const fieldErrors: FieldErrors = {};

  // Handle different error response formats
  if (error && typeof error === 'object') {
    const err = error as Record<string, unknown>;

    // Format 1: { message: string[] } - array of validation messages
    if (Array.isArray(err.message)) {
      const parsedErrors = parseValidationErrors(err.message as string[]);
      for (const parsedError of parsedErrors) {
        const frontendField = mapFieldName(parsedError.field);
        fieldErrors[frontendField] = translateValidationMessage(parsedError, t);
      }
    }

    // Format 2: { message: string } - single error message
    else if (typeof err.message === 'string') {
      const parsed = parseValidationMessage(err.message);
      if (parsed) {
        const frontendField = mapFieldName(parsed.field);
        fieldErrors[frontendField] = translateValidationMessage(parsed, t);
      }
    }

    // Format 3: { errors: { field: string[] } } - field-keyed errors
    else if (err.errors && typeof err.errors === 'object') {
      const errors = err.errors as Record<string, string[]>;
      for (const [field, messages] of Object.entries(errors)) {
        if (Array.isArray(messages) && messages.length > 0) {
          const frontendField = mapFieldName(field);
          const parsed = parseValidationMessage(messages[0]);
          if (parsed) {
            fieldErrors[frontendField] = translateValidationMessage(parsed, t);
          } else {
            fieldErrors[frontendField] = messages[0];
          }
        }
      }
    }
  }

  return fieldErrors;
}
